import { Table } from 'antd';
import styled from 'styled-components';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import DefaultButton from 'components/button/DefaultButton';
import DefaultSelectMenu from 'components/selectMenu/DefaultSelectMenu';
import DefaultInput from 'components/input/DefaultInput';
import Paginav from 'components/paginav/Pagination';
import { phoneAutoHyphen } from 'utils/common';
import { salesColumns } from 'consts/table';
import DatePickerInput from 'components/datePicker/DatePicker';
import { salseTerm } from 'consts/sales';
import { handleDate } from 'utils/date';

interface SalesInforProps {
  startDate: string | null;
  endDate: string | null;
  salesList?: SalesList[];
  pageNumber: number;
  activePeriodButton: number;
  searchOption: string | number;
  selectSearchOptions: SelectOption[];
  searchKeyword: string;
  placeholder?: string;
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  onChangePageNumber: React.Dispatch<React.SetStateAction<number>>;
  onChangeStartDate: (e: any, s: any) => void;
  onChangeEndDate: (e: any, s: any) => void;
  onChangeSearchKeyword: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickReset: () => void;
  onClickSelectPeriod: (index: number) => void;
  onClickGetSalesList: () => void;
  onClickSearchOption: (index: string | number) => void;
}

const SalesDetails = ({
  startDate,
  endDate,
  salesList,
  pageNumber,
  searchOption,
  searchKeyword,
  activePeriodButton,
  selectSearchOptions,
  placeholder,
  totalElements,
  totalPages,
  currentPage,
  pageSize,
  onChangePageNumber,
  onChangeStartDate,
  onChangeEndDate,
  onChangeSearchKeyword,
  onClickReset,
  onClickSelectPeriod,
  onClickGetSalesList,
  onClickSearchOption
}: SalesInforProps) => {
  const cardNumberMaxLength = searchOption === 'cardnum' ? 4 : undefined;

  const dataSource = salesList?.map((item, index: number) => ({
    key: item.epname + index,
    number: (totalElements - ((currentPage - 1) * pageSize + index)).toString(),
    customer: <div>{item.usrname}</div>,
    phonenumber: <div>{phoneAutoHyphen(item.usrphoneno)}</div>,
    trainer: <div>{item.epname}</div>,
    terminalNumber: <div>{item.eTerminalID}</div>,
    date: <div>{handleDate(item.eTradingDate)}</div>,
    code: <div>{item.eApproveAmount}</div>,
    cardCompany: <div>{item.eBuyerInfo}</div>,
    cardNumber: <div>{item.eCardNumberForPrint}</div>,
    fee: <div>{Number(item.eApproveAmount).toLocaleString()}</div>,
    state: <div>{item.eState}</div>,
    type: <div>{item.eTradingType}</div>
  }));

  const handlePageChange = (page: number) => {
    onChangePageNumber(page);
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <h2>매출관리</h2>
        <p>
          B2C 결제 가능 키오스크에서 발생한 매출내역입니다.
          <br />
          결제취소는 단말기가 설치된 키오스크에서 진행해주세요.*
        </p>
        <SearchWrapper>
          <LeftWrapper>
            <div className="buttonWrapper left">
              {salseTerm.map((term) => (
                <div key={`term${term.key}`} onClick={() => onClickSelectPeriod(term.key)}>
                  <DefaultButton
                    text={term.value}
                    size="small"
                    type={term.key === activePeriodButton ? 'primary' : undefined}
                  />
                </div>
              ))}
            </div>
            <DatePickerWrapper>
              <h6>선택기간</h6>
              <DatePickerInput
                value={startDate}
                onChange={onChangeStartDate}
                placeholder={'YYYY-MM-DD'}
              />
              <p>~</p>
              <DatePickerInput
                value={endDate}
                onChange={onChangeEndDate}
                placeholder={'YYYY-MM-DD'}
                endPicker
                limitDate={startDate}
              />
            </DatePickerWrapper>
          </LeftWrapper>
          <RightWrapper>
            <DefaultSelectMenu
              size="small"
              value={searchOption === 'none' ? '선택' : searchOption}
              options={selectSearchOptions}
              onChange={onClickSearchOption}
            />
            <DefaultInput
              height="small"
              name="Trainer's name"
              value={searchKeyword}
              onChange={onChangeSearchKeyword}
              placeholder={placeholder}
              disabled={searchOption === 'none'}
              maxLength={cardNumberMaxLength}
            />
            <div className="buttonWrapper right">
              <DefaultButton
                size="small"
                type="primary"
                text="Search"
                onClick={onClickGetSalesList}
              />
              <DefaultButton size="small" text="Reset" onClick={onClickReset} />
            </div>
          </RightWrapper>
        </SearchWrapper>
      </TitleWrapper>
      {totalPages > 1 && (
        <CountWrapper>
          <div>
            {currentPage}/{totalPages} page
          </div>
        </CountWrapper>
      )}

      <div>
        <Table dataSource={dataSource} bordered columns={salesColumns} pagination={false} />
        {totalPages > 1 && (
          <PaginationWrapper>
            <Paginav
              total={totalElements}
              pageSize={pageSize}
              currentPage={pageNumber}
              onPageChange={handlePageChange}
            />
          </PaginationWrapper>
        )}
      </div>
    </Wrapper>
  );
};

export default SalesDetails;

const Wrapper = styled.div`
  .ant-table-wrapper .ant-table-tbody td {
    padding: 16px 12px;
    vertical-align: middle;
  }

  .ant-select-selection-item {
    font-size: 14px;
  }

  & p {
    color: ${color.black2};
    ${font.body_regular};
  }

  table,
  table thead.ant-table-thead tr th {
    text-align: center;
  }

  table tbody td {
    height: 80px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 24px;
`;

const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .ant-picker {
    padding: 0;

    &.ant-picker-outlined,
    &.ant-picker-outlined:focus,
    &.ant-picker-outlined:focus-within {
      border: none;
      box-shadow: none;
    }

    .ant-picker-input input {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 120px;
      height: 32px;
      padding: 8px 12px;
      border-radius: 4px;
      border: 1px solid rgba(12, 12, 12, 0.2);
      color: ${color.black2};
      ${font.caption2_regular};
    }
  }

  h6 {
    color: ${color.gray700};
    ${font.caption1_regular};
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & div.buttonWrapper {
    display: flex;

    &.left {
      gap: 4px;
    }

    &.right {
      gap: 8px;
    }
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  gap: 24px;
`;

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-select {
    width: 140px;
  }

  .ant-input-affix-wrapper {
    width: 210px;
  }
`;

const CountWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-bottom: 24px;

  color: ${color.black2};
  ${font.caption1_regular};
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin: 24px 0 0;
`;
